/**
 * How far before a scheduled game to create the room so players can join early.
 * Backend creates the game 5 minutes before the scheduled time.
 */
export const PREGAME_WINDOW_MS = 5 * 60 * 1000 // 5 minutes

/**
 * How long after the scheduled start time players are allowed to join.
 * After this window the game is considered finished from the UI perspective,
 * even if the backend cron (which runs every 15 min) hasn't ended the games yet.
 * Must stay in sync with ONE_HOUR_MS in packages/convex/src/gameSchedules/scheduled.ts.
 */
export const JOIN_WINDOW_MS = 60 * 60 * 1000 // 1 hour

/**
 * How far into the past to still show a finished game card.
 * Cards older than this are hidden from the scroll list entirely.
 */
export const CARD_VISIBILITY_WINDOW_MS = 2 * 60 * 60 * 1000 // 2 hours

/**
 * How far ahead of "now" to subscribe to live game data (player counts, game state, winners).
 * Schedules whose next occurrence is further out only need static definitions — live updates
 * become relevant once we get within this window.
 */
export const LIVE_DATA_HORIZON_MS = 4 * 60 * 60 * 1000 // 4 hours

export type GameStatus = {
  type: 'upcoming' | 'live' | 'finished'
  canJoin: boolean
}

/**
 * Derive the display type and join eligibility for a community game card.
 *
 * Timeline:
 *   [pregameStart]──5min──[scheduledTime]──1hr──[joinWindowEnd]
 *        ↑ upcoming/canJoin     ↑ live/canJoin        ↑ finished/no join
 *
 * 'finished' is set as soon as the join window ends, so the UI can show
 * winners or "No winners" without waiting for the backend cron to finish.
 */
export function resolveGameStatus(
  nextOccurrence: number,
  createdGames?: Array<{ gameId: string; name: string; state: string }>
): GameStatus {
  const now = Date.now()
  const joinWindowEnd = nextOccurrence + JOIN_WINDOW_MS
  const pregameStart = nextOccurrence - PREGAME_WINDOW_MS

  const allFinished =
    createdGames !== undefined && createdGames.length > 0 && createdGames.every((g) => g.state === 'finished')

  if (now > joinWindowEnd || allFinished) return { type: 'finished', canJoin: false }
  if (now >= nextOccurrence) return { type: 'live', canJoin: true }
  if (now >= pregameStart) return { type: 'upcoming', canJoin: false }
  return { type: 'upcoming', canJoin: false }
}
